// Deps
import { getResourceQuery, getTaxIdFromSlug } from "~/modules/wordpress";
import { getResourceTypeFromPath } from "~/modules/link-parser";
import limiter from "~/modules/limiter";
import { attachResourceListingPage } from "~/modules/resources";

// Components
import Listing from "~/components/templates/resource/listing";
import PageTransition from "~/components/motion/page-transition";
import Error from "~/pages/_error";

// Contexts
import ContentContext from "~/context/content";
import { FilterContextProvider } from "~/context/resource-filter";

const PostListingPage = ({ data, error, _paging, type, slug, taxonomy }) => {
    if (!data || error) return <Error />;

    // Attach page data, if any, to the response
    data = attachResourceListingPage(data, type);

    return (
        <PageTransition>
            <ContentContext
                layout="listing"
                type={type}
                data={data}
                taxonomy={{ slug, type: taxonomy }}
            >
                <FilterContextProvider>
                    <Listing resources={data} paging={_paging} type={type} />
                </FilterContextProvider>
            </ContentContext>
        </PageTransition>
    );
};

PostListingPage.getInitialProps = async ({ asPath, query }) => {
    // Listing pages support an optional pagination param
    const page = query.page || 1;

    const taxSlug = query.slug;

    // Determine what resource type we're requesting by the route path base
    const type = getResourceTypeFromPath(asPath);

    try {
        /**
         * If there's a category or tag slug, we need to fetch
         * the ID of that Term so we cna filter our request later
         */
        let tax = null;
        let termType = null;
        if (["categories", "tags"].includes(type)) {
            await limiter();

            tax = await getTaxIdFromSlug({
                tax: type,
                slug: taxSlug
            })().get();

            if (!tax || !tax.length) {
                // No taxonomy exists for this slug
                console.log(`[404]: ${type} listing, ${asPath}`);
                return { error: 404 };
            } else {
                tax = tax[0];
            }
        }

        await limiter();

        // Setup the main resource listing query
        let xhrQuery = getResourceQuery({
            type: tax ? "posts" : type, // only posts use category or tag
            page // pagination count
        })();

        // Add some extra parameters to the query
        if (type == "categories") {
            xhrQuery = xhrQuery.categories(tax.id);
            termType = "category";
        } else if (type == "tags") {
            xhrQuery = xhrQuery.tags(tax.id);
            termType = "tag";
        }

        // Glossary is sorted by meta value alphabetically
        if (type === "glossary") {
            xhrQuery = xhrQuery
            .param("orderby", "meta_value")
            .param("meta_key", "glossary-term")
            .param("order", "asc")
        }

        let resources = await xhrQuery.get();

        if (!resources || !resources.length) {
            console.log(`[404]: ${type} listing, ${asPath}`);
            return { error: 404 };
        }

        // Attach page data, if any, to the response
        resources = attachResourceListingPage(resources, type);

        return {
            data: resources,
            _paging: resources._paging,
            type: tax ? "posts" : type,
            slug: taxSlug,
            taxonomy: termType
        };
    } catch (e) {
        console.log(`[${e.status}]: ${type} listing, ${asPath}`);
        return { error: e.status };
    }
};

export default PostListingPage;
